import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { getApiUrl } from "./config";

interface ApiResponse<T> {
  success: boolean;
  data: T;
  errors?: Array<{ field?: string; message: string; code?: string }>;
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function isWrappedResponse(obj: unknown): obj is { success: boolean; data: unknown } {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "success" in obj &&
    "data" in obj
  );
}

function extractData<T>(json: unknown): T {
  if (isWrappedResponse(json)) {
    return json.data as T;
  }
  return json as T;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  customHeaders?: Record<string, string>,
): Promise<Response> {
  const fullUrl = url.startsWith("http") ? url : getApiUrl(url);
  
  const headers: Record<string, string> = {
    ...getAuthHeaders(),
    ...(customHeaders ?? {}),
    ...(data ? { "Content-Type": "application/json" } : {}),
  };
  
  const res = await fetch(fullUrl, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

export async function apiRequestJson<T>(
  method: string,
  url: string,
  data?: unknown | undefined,
  customHeaders?: Record<string, string>,
): Promise<T> {
  const res = await apiRequest(method, url, data, customHeaders);
  const json = await res.json();
  return extractData<T>(json);
}

type UnauthorizedBehavior = "returnNull" | "throw";

export function getQueryFn<T>(options: {
  on401: UnauthorizedBehavior;
}): QueryFunction<T> {
  return async ({ queryKey }) => {
    const path = queryKey.join("/");
    const fullUrl = path.startsWith("http") ? path : getApiUrl(path);
    
    const res = await fetch(fullUrl, {
      headers: getAuthHeaders(),
      credentials: "include",
    });

    if (options.on401 === "returnNull" && res.status === 401) {
      return null as T;
    }

    await throwIfResNotOk(res);
    const json = await res.json();
    return extractData<T>(json);
  };
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
